import classNames from "classnames";
import { forwardRef, memo } from "react";
import SVG from "react-inlinesvg";
import Select, { components } from "react-select";
import CreatableSelect from "react-select/creatable";
import IconButton from "../IconButton";
import { selectFocusStyle } from "../../styles/a11y";
import { StyledContainer, StyledOption } from "./styles";

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <SVG src="/assets/settings_dropdown_icon.svg" />
    </components.DropdownIndicator>
  );
};

const CustomOption = (props) => {
  const { data, innerRef, innerProps, isSelected, isFocused, selectProps } = props;
  const isCustom = data.label === "Custom";
  const useNeutralSelectedOption = Boolean(selectProps.useNeutralSelectedOption);
  const selectedTextColor = useNeutralSelectedOption ? "var(--grey-0)" : "white";
  const selectedBackgroundColor = useNeutralSelectedOption ? "var(--menu-color-hover)" : "var(--primary-cta)";

  return (
    <StyledOption
      ref={innerRef}
      {...innerProps}
      style={{
        color: isSelected ? selectedTextColor : isCustom ? "var(--primary-cta)" : "var(--grey-0)",
        backgroundColor: isSelected ? selectedBackgroundColor : isFocused ? "var(--menu-color-hover)" : "var(--menu-color)",
      }}
      className={classNames("option-wrapper", isCustom ? "custom-option" : undefined, isCustom && isSelected ? "custom-option-selected" : undefined)}
    >
      {isCustom && (
        <IconButton
          src="/assets/add.svg"
          size={16}
          className='mr-2'
        />
      )}
      {selectProps.formatOptionLabel ? selectProps.formatOptionLabel(data, { context: "menu" }) : <span>{data.label}</span>}
    </StyledOption>
  );
};

const StyledSelect = forwardRef((props, ref) => {
  const { showFullFormatInValue = true, ...restProps } = props;
  const groupStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "16px",
  };
  const themeFunction = (theme) => ({
    ...theme,
    colors: {
      ...theme.colors,
      neutral0: "var(--menu-color)",
      neutral30: "var(--grey-2)",
      neutral20: "var(--grey-2)",
      primary: "var(--primary-cta)",
      primary25: "var(--menu-color-hover)",
      primary50: "var(--menu-color-active)",
    },
  });
  const defaultOptionLabel = (data) => <div style={groupStyles}>{data.label}</div>;
  const backgroundColorOverride = props.styleOverrides ? { backgroundColor: props.styleOverrides.backgroundColor } : {};
  const styleObject = {
    control: (base, state) => ({
      ...base,
      border: "1.5px solid var(--grey-3)",
      display: "flex",
      borderRadius: "8px",
      backgroundColor: "var(--menu-color)",
      ...backgroundColorOverride,
      ...selectFocusStyle(state),
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--grey-0)",
      lineHeight: "1.3",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      display: props.readOnly ? "none" : "flex",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: "none",
    }),
    option: (base, state) => ({
      ...base,
      color: state.isSelected ? "var(--selected-text-color)" : "var(--grey-0)",
      backgroundColor: state.isSelected ? "var(--primary-cta)" : state.isFocused ? "var(--menu-color-hover)" : "var(--menu-color)",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "10px",
      overflow: "hidden",
      paddingTop: "0px",
      boxShadow: "var(--default-drop-shadow)",
      backgroundColor: "var(--menu-color)",
      zIndex: 30000, // nothing should go above this
    }),
    menuList: (base) => ({
      ...base,
      paddingTop: "0px",
      paddingBottom: "0px",
    }),
    ...props.styleObjectOverrides,
  };

  const formatOptionLabel = (data, meta) => {
    if (!props.formatOptionLabel) return defaultOptionLabel(data);

    // If showFullFormatInValue is false and we're in the value context, just show the label
    if (!showFullFormatInValue && meta.context === "value") {
      return data.label;
    }

    return props.formatOptionLabel(data, meta);
  };

  return (
    <StyledContainer
      ref={ref}
      className="styled-select-container"
    >
      {props.creatable ? (
        <CreatableSelect
          {...restProps}
          formatOptionLabel={formatOptionLabel}
          styles={styleObject}
          theme={themeFunction}
          components={{
            DropdownIndicator,
            Option: CustomOption,
          }}
          value={props.value}
          onChange={props.onChange}
          options={props.options}
          closeMenuOnSelect={true}
        />
      ) : (
        <Select
          {...restProps}
          formatOptionLabel={formatOptionLabel}
          styles={styleObject}
          theme={themeFunction}
          components={{
            DropdownIndicator,
            Option: CustomOption,
          }}
          value={props.value}
          onChange={props.onChange}
          options={props.options}
          closeMenuOnSelect={true}
        />
      )}
    </StyledContainer>
  );
});

export default memo(StyledSelect);
